import { ReactElement } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "@/utils/toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useEffect } from "react";
import { getApiUrl } from "@/utils/api";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { companyAtom } from "@/store/company";

interface FacilitiesFormProps {
  facilityId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

// Define Company interface
interface Company {
  id: number;
  name: string;
}

// Define Country interface
interface Country {
  id: number;
  name: string;
}

// Define API response interface
interface ApiResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const facilitySchema = z.object({
  name: z.string().min(1, "Name is required"),
  fiscal_name: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  fiscal_email: z
    .string()
    .email("Invalid email")
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val))
    .or(z.literal("")),
  vat: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  address: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  cp: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  city: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  province: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  country: z.number().optional().nullable(),
  phone: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  mobile: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  email: z.string().email("Invalid email"),
  logo: z.any().optional(),
  is_active: z.boolean(),
  company: z.number().optional(),
  lat: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
  lng: z
    .string()
    .optional()
    .nullable()
    .transform((val) => (val === null ? "" : val)),
});

type FacilityFormValues = z.infer<typeof facilitySchema>;

export default function FacilitiesForm({
  facilityId: propFacilityId,
  onSuccess,
  onCancel,
}: FacilitiesFormProps): ReactElement {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [companyAtomValue] = useAtom(companyAtom);

  // Check if user is admin
  const isAdmin = user?.rol === "admin";

  // Get facilityId from navigation state if available
  const stateFacilityId = location.state?.facilityId;

  // Use the facilityId from props or from navigation state
  const facilityId = propFacilityId || stateFacilityId;

  const form = useForm<FacilityFormValues>({
    resolver: zodResolver(facilitySchema),
    defaultValues: {
      name: "",
      fiscal_name: "",
      fiscal_email: "",
      vat: "",
      address: "",
      cp: "",
      city: "",
      province: "",
      country: undefined,
      phone: "",
      mobile: "",
      email: "",
      logo: "",
      is_active: true,
      company: undefined,
      lat: "",
      lng: "",
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["facility", facilityId],
    queryFn: async () => {
      if (!facilityId) return null;
      const response = await fetch(getApiUrl(`facilities/${facilityId}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch facility");
      }
      return response.json();
    },
    enabled: !!facilityId,
  });

  // Fetch companies for admin users
  const { data: companiesData } = useQuery<ApiResponse<Company>>({
    queryKey: ["companies"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("companies/"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch companies");
      }
      return response.json();
    },
    enabled: isAdmin, // Only fetch if user is admin
  });

  // Extract companies from the response
  const companies = companiesData?.results || [];

  // Fetch countries
  const { data: countriesData, isLoading: isLoadingCountries } = useQuery<
    ApiResponse<Country>
  >({
    queryKey: ["countries"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("countries/?limit=9999"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch countries");
      }
      return response.json();
    },
    enabled: !!token, // Fetch if token exists
  });

  // Extract countries from the response
  const countries = countriesData?.results || [];

  useEffect(() => {
    if (data) {
      console.log("Facility data:", data);
      form.reset({
        ...data,
        country: data.country,
        company: data.company?.id,
      });
      console.log("Form values after reset:", form.getValues());
    } else if (!isAdmin && user?.company) {
      // Set default company for non-admin users
      form.setValue("company", user.company.id);
    } else if (!facilityId && companyAtomValue?.id) {
      // When creating a new facility, if companyAtom has a value, use it
      form.setValue("company", Number(companyAtomValue.id));
    }
  }, [data, form, isAdmin, user, facilityId, companyAtomValue]);

  const mutation = useMutation({
    mutationFn: async (values: FacilityFormValues) => {
      const url = facilityId
        ? getApiUrl(`facilities/${facilityId}/`)
        : getApiUrl("facilities/");
      const method = facilityId ? "PATCH" : "POST";

      const formData = new FormData();
      formData.append("name", values.name);
      // Use selected company for admin users or user's company for non-admin users
      formData.append(
        "company",
        values.company?.toString() || user?.company?.id.toString() || "",
      );
      formData.append("email", values.email);
      if (values.fiscal_name) {
        formData.append("fiscal_name", values.fiscal_name);
      }
      if (values.fiscal_email) {
        formData.append("fiscal_email", values.fiscal_email);
      }
      if (values.vat) {
        formData.append("vat", values.vat);
      }
      if (values.address) {
        formData.append("address", values.address);
      }
      if (values.cp) {
        formData.append("cp", values.cp);
      }
      if (values.city) {
        formData.append("city", values.city);
      }
      if (values.province) {
        formData.append("province", values.province);
      }
      if (values.country) {
        formData.append("country", values.country.toString());
      }
      if (values.phone) {
        formData.append("phone", values.phone);
      }
      if (values.mobile) {
        formData.append("mobile", values.mobile);
      }
      if (values.lat) {
        formData.append("lat", values.lat);
      }
      if (values.lng) {
        formData.append("lng", values.lng);
      }
      formData.append("is_active", values.is_active.toString());
      // Solo agregar el logo si es un archivo válido
      if (values.logo instanceof File) {
        formData.append("logo", values.logo);
      } else if (
        values.logo &&
        typeof values.logo === "string" &&
        !values.logo.startsWith("http")
      ) {
        // Handle base64 or other string format that isn't a URL
        formData.append("logo", values.logo);
      }

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to save facility");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facilities"] });
      toast.success(
        `Facility ${facilityId ? "updated" : "created"} successfully`,
      );
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = (values: FacilityFormValues) => {
    mutation.mutate(values);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Form {...form}>
      {stateFacilityId && (
        <div className="sm:flex sm:items-center mb-8">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold">
              {t("common.my-facility")}
            </h1>
          </div>
        </div>
      )}

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full pb-24 overflow-y-auto"
      >
        <div className="space-y-6 overflow-y-auto flex-1 px-4">
          {isAdmin && (
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => {
                console.log("Company field value:", field.value);
                // Check if we should disable the company select
                // const shouldDisable = !facilityId && !!companyAtomValue?.id;
                const shouldDisable = true;
                return (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(value) => {
                          console.log("Selected company value:", value);
                          field.onChange(Number(value));
                        }}
                        value={field.value ? field.value.toString() : undefined}
                        disabled={shouldDisable}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies?.map((company: Company) => (
                            <SelectItem
                              key={company.id}
                              value={company.id.toString()}
                            >
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.name")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.email")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    {...field}
                    readOnly={!!facilityId}
                    className={facilityId ? "bg-muted" : ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.phone")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fiscal_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.fiscal_name")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fiscal_email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.fiscal_email")}</FormLabel>
                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="vat"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.vat")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.address")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cp"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.cp")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.city")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="province"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("companies.fields.province")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("facilities.fields.country")}</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(Number(value))}
                  value={field.value?.toString()}
                  disabled={isLoadingCountries}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t("facilities.fields.country")}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {isLoadingCountries ? (
                      <SelectItem value="loading" disabled>
                        {t("facilities.fields.loading_countries")}
                      </SelectItem>
                    ) : (
                      countries.map((country) => (
                        <SelectItem
                          key={country.id}
                          value={country.id.toString()}
                        >
                          {country.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="lat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("facilities.fields.latitude") || "Latitude"}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. 41.3851" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lng"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("facilities.fields.longitude") || "Longitude"}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. 2.1734" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="mobile"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="logo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Logo</FormLabel>
                <FormControl>
                  <div className="flex flex-col gap-2">
                    {data?.logo && (
                      <img
                        src={data.logo}
                        alt="Receipt preview"
                        className="w-32 h-32 object-cover rounded"
                      />
                    )}
                    <Input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        field.onChange(file || null);
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={(value) => field.onChange(value === "true")}
                    value={field.value ? "true" : "false"}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="fixed inset-x-0 bottom-0 bg-background p-4 border-t flex justify-end space-x-2 mt-auto">
          <Button variant="outline" type="button" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="default" type="submit">
            {facilityId ? t("common.update") : t("common.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
